import { AlertTriangle, LogOut, Paperclip, Undo2 } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ASSET_OWNERSHIP_LABELS,
  CLEARANCE_STATUSES,
  CLEARANCE_STATUS_LABELS,
  formatPlainDate,
  isCompanyProperty,
  today,
  type ClearanceStatus,
} from '@cfl/assets-shared';

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { TeamArt } from '@/components/illustrations';
import { Documents } from '@/components/documents';
import { useDocuments } from '@/lib/documents-api';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { EmployeePicker } from '@/components/pickers';
import { PageContainer } from '@/components/shell/page-container';
import { StatTile } from '@/components/stat-tile';
import { clearanceVariant, PILL_CLASS } from '@/lib/badges';
import { ApiError } from '@/lib/api';
import { useEmployees } from '@/lib/employees-api';
import {
  useExits,
  useExitSummary,
  usePendingAssets,
  useRecordExit,
  useReinstate,
  type ExitRecord,
} from '@/lib/operations-api';

export function ExitsPage() {
  const { data: exits } = useExits();
  const { data: summary } = useExitSummary();
  const [recording, setRecording] = React.useState(false);
  const reinstate = useReinstate();
  // Reinstating revokes every session that person has, so it does not fire off a
  // single click in a list of rows that all look alike.
  const [reinstating, setReinstating] = React.useState<ExitRecord | null>(null);

  const stillHolding = exits?.filter((e) => e.outstandingNow > 0) ?? [];

  return (
    <PageContainer>
      <div className="grid gap-4 sm:grid-cols-2">
        <StatTile
          label="Left, still holding"
          value={summary?.exitedHoldingAssets ?? 0}
          caption={
            summary?.exitedHoldingAssets
              ? 'exited employees who never returned everything'
              : 'everyone who left returned everything'
          }
          tone={summary?.exitedHoldingAssets ? 'critical' : 'good'}
        />
        <StatTile
          label="Clearance not final"
          value={summary?.pendingClearance ?? 0}
          caption="exits still to be signed off as fully cleared"
          tone={summary?.pendingClearance ? 'warning' : 'good'}
        />
      </div>

      <div>
        <Button hint={"Record that someone is leaving, with what they must hand back"} size="sm" onClick={() => setRecording(true)}>
          <LogOut className="mr-1.5 size-4" /> Record an exit
        </Button>
      </div>

      {stillHolding.length > 0 && (
        <Card className="border-destructive/30">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="size-4 text-destructive" />
              <CardTitle className="text-sm">Left the company still holding assets</CardTitle>
            </div>
            <CardDescription className="text-xs">
              Recover these, then update the clearance — it can only become Cleared once
              nothing is outstanding.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="divide-y">
              {stillHolding.map((exit) => (
                <li key={exit.id} className="flex flex-wrap items-center gap-3 py-2.5">
                  <Link
                    to={`/employees/${exit.employee.id}`}
                    className="min-w-0 flex-1 hover:underline"
                  >
                    <div className="text-sm font-medium">{exit.employee.name}</div>
                    <div className="at-num text-xs text-muted-foreground">
                      {exit.employee.employeeCode} · left {formatPlainDate(exit.exitDate)}
                    </div>
                  </Link>
                  <Badge variant="soft-destructive" className={PILL_CLASS}>
                    {exit.outstandingNow} still out
                  </Badge>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Exit records</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {exits?.length ? (
            <ul className="divide-y">
              {exits.map((exit) => (
                <li key={exit.id} className="py-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <Link
                      to={`/employees/${exit.employee.id}`}
                      className="min-w-0 flex-1 hover:underline"
                    >
                      <div className="text-sm font-medium">{exit.employee.name}</div>
                      <div className="at-num text-xs text-muted-foreground">
                        {exit.employee.employeeCode} · {formatPlainDate(exit.exitDate)}
                        {exit.reason ? ` · ${exit.reason}` : ''}
                      </div>
                    </Link>

                    <Badge variant={clearanceVariant(exit.clearanceStatus)} className={PILL_CLASS}>
                      {CLEARANCE_STATUS_LABELS[exit.clearanceStatus]}
                    </Badge>

                    {/* The two numbers say different things and both matter: what was
                        missing on the day, and what is still missing now. */}
                    {exit.outstandingAtClearance > 0 && (
                      <span className="at-num text-xs text-muted-foreground">
                        {exit.outstandingAtClearance} out then · {exit.outstandingNow} now
                      </span>
                    )}

                    {exit.employee.status === 'EXITED' && (
                      <Button hint={"Undo this exit — the person is active again"}
                        size="sm"
                        variant="ghost"
                        disabled={reinstate.isPending}
                        onClick={() => setReinstating(exit)}
                      >
                        <Undo2 className="mr-1.5 size-3.5" /> Reinstate
                      </Button>
                    )}
                  </div>

                  <ExitDocuments exitId={exit.id} />
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState art={TeamArt} title="Nobody has left yet" body="When someone leaves, record it here — the clearance checklist makes sure every company device comes back before they are cleared." />
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={reinstating !== null}
        onOpenChange={(open) => !open && setReinstating(null)}
        title={`Reinstate ${reinstating?.employee.name ?? ''}?`}
        description={`They left on ${reinstating ? formatPlainDate(reinstating.exitDate) : ''}.`}
        consequence="Their account becomes active again and they can be issued assets. The exit record is kept - it happened, and deleting it would hide a rehire from anyone reading the history later."
        confirmLabel="Reinstate"
        successMessage={`${reinstating?.employee.name ?? 'They'} reinstated.`}
        onConfirm={() => reinstate.mutateAsync(reinstating!.employee.id)}
      />

      <RecordExitDialog open={recording} onOpenChange={setRecording} />
    </PageContainer>
  );
}

/**
 * The exit form.
 *
 * The pending-asset list is re-read here rather than trusted from the directory,
 * because between opening this dialog and submitting it IT may have taken the laptop
 * back - or issued another one. The API checks it again inside the transaction; this
 * is so the person filling the form is not surprised by that refusal.
 */
/**
 * Recording an exit, from the exits screen or from one person's own page.
 *
 * `employee` set means it was opened against a known person - so there is no picker,
 * and no thousand-row fetch to fill one. The outstanding-assets check below is
 * unchanged either way: it is keyed off the chosen id, which is now simply already
 * chosen.
 */
export function RecordExitDialog({
  employee,
  open,
  onOpenChange,
}: {
  employee?: { id: string; name: string; employeeCode: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const record = useRecordExit();
  const { data: employees } = useEmployees(
    { pageSize: 1000 },
    { enabled: employee === undefined },
  );
  const [employeeId, setEmployeeId] = React.useState(employee?.id ?? '');
  const [form, setForm] = React.useState({
    exitDate: today(),
    lastWorkingDate: '',
    reason: '',
    clearanceStatus: 'PENDING' as ClearanceStatus,
    comments: '',
  });

  const { data: pending } = usePendingAssets(employeeId || null);
  // Company property only, which is the same count the API checks before it will
  // write CLEARED. Their own laptop is still listed below - HR is talking about all
  // of it - but the company is not owed it back, so it cannot block the clearance.
  const outstanding = pending?.filter((p) => isCompanyProperty(p.asset.ownership)).length ?? 0;

  React.useEffect(() => {
    if (open) {
      setEmployeeId(employee?.id ?? '');
      setForm({
        exitDate: today(),
        lastWorkingDate: '',
        reason: '',
        clearanceStatus: 'PENDING',
        comments: '',
      });
    }
  }, [open, employee?.id]);

  const clearedBlocked = form.clearanceStatus === 'CLEARED' && outstanding > 0;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!employeeId) {
      toast.error('Choose an employee.');
      return;
    }
    try {
      await record.mutateAsync({
        employeeId,
        exitDate: form.exitDate,
        lastWorkingDate: form.lastWorkingDate || null,
        reason: form.reason.trim() || null,
        clearanceStatus: form.clearanceStatus,
        comments: form.comments.trim() || null,
      });
      toast.success('Exit recorded.');
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'That could not be recorded.');
    }
  };

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {employee ? `Record ${employee.name}'s exit` : 'Record an exit'}
          </DialogTitle>
          <DialogDescription>
            {employee ? (
              <>
                <span className="at-num">{employee.employeeCode}</span> — this marks them
              </>
            ) : (
              'This marks the employee'
            )}{' '}
            as exited and revokes their access. Their record and history are kept.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          {/* No picker when the person is already settled — the title names them. */}
          {!employee && (
            <div className="space-y-1.5">
              <Label>Employee</Label>
              <EmployeePicker
                employees={employees?.employees}
                value={employeeId}
                onChange={setEmployeeId}
              />
            </div>
          )}

          {employeeId && (
            <Alert className={outstanding > 0 ? 'border-warning/40 bg-warning/5' : undefined}>
              <AlertDescription className="text-xs">
                <span className="font-medium">
                  {outstanding > 0
                    ? `Still holding ${outstanding} of our asset${outstanding === 1 ? '' : 's'}:`
                    : pending?.length
                      ? 'Nothing of ours is outstanding — this exit can be fully cleared. What they still have is their own:'
                      : 'Nothing is outstanding — this exit can be fully cleared.'}
                </span>
                {/* Everything open is listed, their own devices included: HR is having
                    this conversation about all of it, and a device that vanishes from
                    the screen is one nobody remembers to ask about. Only the company’s
                    kit is counted, because it is the only part we can ask back. */}
                {pending && pending.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {pending.map((p) => (
                      <li key={p.assignmentId} className="at-num">
                        {p.asset.code} — {p.asset.name}
                        {!isCompanyProperty(p.asset.ownership) && (
                          <span className="text-muted-foreground">
                            {' · '}
                            {ASSET_OWNERSHIP_LABELS.EMPLOYEE}, stays with them
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </AlertDescription>
            </Alert>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Exit date</Label>
              <Input
                type="date"
                value={form.exitDate}
                max={today()}
                onChange={(e) => set('exitDate', e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>Last working date</Label>
              <Input
                type="date"
                value={form.lastWorkingDate}
                onChange={(e) => set('lastWorkingDate', e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Reason</Label>
            <Input
              value={form.reason}
              onChange={(e) => set('reason', e.target.value)}
              placeholder="Resignation, end of contract…"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Clearance</Label>
            <Select
              value={form.clearanceStatus}
              onValueChange={(v) => set('clearanceStatus', v as ClearanceStatus)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CLEARANCE_STATUSES.map((status) => (
                  <SelectItem key={status} value={status} disabled={status === 'CLEARED' && outstanding > 0}>
                    {CLEARANCE_STATUS_LABELS[status]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {clearedBlocked && (
              <p className="text-[11px] leading-relaxed text-destructive">
                Cleared means nothing is outstanding, and {outstanding} company asset
                {outstanding === 1 ? ' is' : 's are'} still issued to this person. Record the
                return first, or choose “Cleared with comments” and say what is missing.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Clearance comments</Label>
            <Textarea
              rows={3}
              value={form.comments}
              onChange={(e) => set('comments', e.target.value)}
              placeholder="All assets returned and verified. No dues."
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button hint={"Record the exit and what they must hand back"} type="submit" disabled={record.isPending || clearedBlocked}>
              {record.isPending ? 'Recording…' : 'Record exit'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The signed clearance form for one exit, collapsed by default.
 *
 * Collapsed because most rows on this screen are being scanned, not opened - an
 * upload control under all thirty exits would bury the thing the page is actually
 * for, which is seeing who left owing what. The count is on the trigger, so a record
 * that HAS its paperwork can be told from one that does not without expanding it.
 */
function ExitDocuments({ exitId }: { exitId: string }) {
  const [open, setOpen] = React.useState(false);
  // Only fetched once expanded: thirty exit rows would otherwise be thirty requests
  // on a page where most people never open one.
  const { data } = useDocuments('exit', exitId, open);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <Button hint={"Show the clearance documents for this exit"} size="sm" variant="ghost" className="mt-1 h-7 -ml-2 text-xs text-muted-foreground">
          <Paperclip className="mr-1.5 size-3.5" />
          {open ? 'Hide documents' : 'Clearance documents'}
          {data && data.length > 0 ? <span className="at-num ml-1">({data.length})</span> : null}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2">
        <Documents
          entity="exit"
          entityId={exitId}
          canManage
          noun="clearance"
          emptyHint="No signed clearance attached. This is the record that says what was agreed on the day - a scan or a photo of the signed form is enough."
        />
      </CollapsibleContent>
    </Collapsible>
  );
}
